import { DEFAULT_BUILD_RATES } from "./buildCost";
/* Rate card + scope→fee model for the project cost estimator.
   Design fees are a % of construction budget, with a floor fee, a complexity
   multiplier and optional add-ons. The fee is disbursed across professional work
   phases (Schematic → Design Development → Construction Documents/IFT → Tender →
   Construction). The rate card is editable (stored in the app state) — these are
   the studio defaults. */

export interface Discipline { key: string; label: string; blurb: string; pct: number; floor: number; core: boolean; }
export interface AddOn { key: string; label: string; blurb: string; kind: "pct" | "fixed"; value: number; requires?: string; }
export interface Complexity { key: string; label: string; blurb: string; mult: number; }
export interface FeePhase { key: string; label: string; pct: number; construction?: boolean; }
/** An out-of-scope charge — work that falls outside the agreed scope, billed per
 *  occurrence as-and-when instructed. NOT part of the professional fee. */
export interface AdditionalService { key: string; label: string; blurb: string; unit: string; rate: number; included?: number; }
import { fxFactor, DEFAULT_RATE_CURRENCY } from "./buildCost";

export interface RateCard {
  disciplines: Discipline[];
  addons: AddOn[];
  complexity: Complexity[];
  phases: FeePhase[];
  services: AdditionalService[];
  /** Construction cost per unit area, by building type. Studio-owned: the client
   *  moves the budget, never the rate. */
  buildRates?: import("./buildCost").BuildRate[];
  /** The currency the construction rates are written in (USD unless changed). */
  buildRateCurrency?: string;
  /** Units of each currency per 1 USD — used to price a project in its own
   *  currency from a rate card written in another. */
  fx?: Record<string, number>;
}

export const DEFAULT_DISCIPLINES: Discipline[] = [
  { key: "architecture", label: "Architecture", blurb: "Concept, developed design & documentation.", pct: 0.060, floor: 12000, core: true },
  { key: "interior",     label: "Interior design", blurb: "Spatial detailing, joinery, materials & FF&E.", pct: 0.035, floor: 8000, core: true },
  { key: "structural",   label: "Structural engineering", blurb: "Structural scheme, analysis & details.", pct: 0.015, floor: 5000, core: true },
  { key: "mep",          label: "MEP design", blurb: "Mechanical, electrical & plumbing design.", pct: 0.020, floor: 5000, core: true },
  { key: "landscape",    label: "Landscape", blurb: "External works, planting & hardscape.", pct: 0.012, floor: 3500, core: false },
  { key: "consultancy",  label: "Consultancy & PM", blurb: "Project management & contract administration.", pct: 0.025, floor: 6000, core: false },
];

export const DEFAULT_ADDONS: AddOn[] = [
  { key: "viz",        label: "3D visualization package", blurb: "Photoreal renders of the key spaces.", kind: "fixed", value: 4800 },
  { key: "lighting",   label: "Lighting design", blurb: "Layered architectural lighting scheme.", kind: "pct", value: 0.008, requires: "interior" },
  { key: "bim",        label: "BIM coordination", blurb: "Federated model & clash detection.", kind: "pct", value: 0.010 },
  { key: "supervision",label: "Site supervision", blurb: "Periodic site inspection through construction.", kind: "pct", value: 0.015, requires: "architecture" },
  { key: "sustainability", label: "Sustainability & energy", blurb: "Passive strategy & energy modelling.", kind: "fixed", value: 6500 },
  { key: "joinery",    label: "Bespoke joinery detailing", blurb: "Shop drawings for custom joinery.", kind: "pct", value: 0.010, requires: "interior" },
];

export const DEFAULT_COMPLEXITY: Complexity[] = [
  { key: "standard", label: "Standard", blurb: "A resolved, well-detailed scheme.", mult: 1.0 },
  { key: "premium",  label: "Premium", blurb: "Elevated detail & bespoke elements.", mult: 1.18 },
  { key: "bespoke",  label: "Bespoke", blurb: "Fully bespoke, high-craft throughout.", mult: 1.4 },
];

/** Scope of services per discipline (HD Studio service schedule) — used in the engagement. */
export const DISCIPLINE_SCOPE: Record<string, string[]> = {
  architecture: ["Site analysis and feasibility studies", "Schematic design and concept planning", "Detailed architectural drawings and construction documents", "Coordination with consultants and engineers", "Compliance with local codes and regulations", "Permit and approval assistance", "Project oversight and construction administration"],
  interior: ["Concept development and space planning", "Selection of furniture, finishes and materials", "Custom millwork and built-in design", "Lighting and fixture selection", "3D renderings of the key spaces (one per room included)", "Colour palettes and mood boards", "Procurement consultation and project coordination"],
  structural: ["Structural analysis and load calculations", "Foundation and framing system design", "Seismic and wind load assessments", "Retrofitting and reinforcement solutions", "Coordination with architects and MEP teams", "Construction documentation and site inspections", "Compliance with local building codes and regulations"],
  mep: ["HVAC system design and load calculations", "Electrical power distribution and lighting design", "Plumbing and water supply systems", "Energy efficiency and sustainability consultation", "Fire protection and suppression system design", "Code compliance and safety evaluations", "Coordination with other trades and disciplines"],
  landscape: ["External works, hardscape and planting design", "Site grading and drainage strategy", "Coordination with the architectural scheme"],
  consultancy: ["Project management and programme control", "Contract administration and progress reporting", "Cost monitoring against the budget"],
};

/** What each design stage delivers — shown in quotations, the engagement & Stages. */
export const PHASE_OUTLINE: Record<string, string> = {
  sd: "Establish the design concept — spatial planning, massing and the overall architectural direction. Preliminary plans, sections and 3D studies developed for your review and sign-off before the scheme is resolved.",
  dd: "Resolve the scheme across every space — layouts, materials and finishes fixed and coordinated. Developed drawings and outline specifications that lock the design.",
  cd: "Produce the full technical drawing set and specifications required to build and tender — the Issued-for-Tender (IFT) package, with schedules and details.",
  tender: "Issue the documents to contractors, manage the tender process, answer queries and addenda, review returns on a like-for-like basis and support the award of the building contract.",
  ca: "Support delivery on site — periodic inspections, responding to RFIs, reviewing shop drawings and samples, and quality oversight through to practical completion and handover.",
};

/** The full picture of a work stage — what it's for, what we do, what you get.
 *  Powers the stage detail lightbox on the quotation. */
/** A line that only belongs in the scope when that discipline was appointed.
 *  A client who never engaged us for structure must not be told we will do a
 *  "preliminary structural strategy" — it reads as a promise we never made. */
export type ScopedLine = string | { text: string; needs: string };
export interface PhaseDetail { purpose: string; activities: ScopedLine[]; outputs: ScopedLine[]; client: string; }
export const PHASE_DETAIL: Record<string, PhaseDetail> = {
  sd: {
    purpose: "Establish the architectural idea and prove the accommodation works on the site, within the budget and the planning constraints.",
    activities: ["Site appraisal — orientation, access, levels and constraints", "Test-fit of the accommodation schedule against the brief", "Massing and spatial planning options, compared and narrowed", { text: "Preliminary structural strategy", needs: "structural" }, { text: "Preliminary building services strategy", needs: "mep" }, "Indicative cost check against the construction budget"],
    outputs: ["Concept plans, sections and elevations at outline scale", "Massing model and 3D studies of the principal spaces", "Design statement setting out the architectural direction", "Updated accommodation schedule with areas"],
    client: "Review the concept and sign it off — this is the cheapest point at which to change direction.",
  },
  dd: {
    purpose: "Resolve the signed-off concept into a fully coordinated design, with every space, material and system fixed.",
    activities: ["Develop each space in plan, section and elevation", "Fix materials, finishes and the key joinery", { text: "Coordinate the structural design through the building", needs: "structural" }, { text: "Coordinate the building services through the design", needs: "mep" }, { text: "Consultant workshops and clash review", needs: "any-consultant" }, "Cost plan updated against the developed design"],
    outputs: ["Developed plans, sections and elevations", "Outline specification and finishes schedule", "Room data sheets for the principal spaces", { text: "Coordinated structural layouts", needs: "structural" }, { text: "Coordinated MEP layouts", needs: "mep" }],
    client: "Approve the layouts, materials and finishes. Changes instructed after sign-off are variations.",
  },
  cd: {
    purpose: "Produce the technical set a contractor can price and build from without ambiguity.",
    activities: ["Full technical drawing production", "Construction details, junctions and assemblies", "Schedules — doors, windows, finishes, sanitary and joinery", "Specification writing and coordination", { text: "Final inter-disciplinary coordination and checking", needs: "any-consultant" }],
    outputs: ["Issued-for-Tender (IFT) architectural set", { text: "Structural drawings and schedules", needs: "structural" }, { text: "MEP drawings and schedules", needs: "mep" }, "Full specification", "Schedules and detail sheets"],
    client: "Little input needed — this is the studio's production stage.",
  },
  tender: {
    purpose: "Take the documented design to market and secure a contractor on a fair, comparable basis.",
    activities: ["Issue the tender package and manage the query process", "Prepare addenda and clarifications", "Receive and review returns on a like-for-like basis", "Prepare a tender report with a recommendation", "Support the award of the building contract"],
    outputs: ["Tender package and issue register", "Addenda and query responses", "Tender comparison report and recommendation"],
    client: "Choose the contractor, with our recommendation in hand.",
  },
  ca: {
    purpose: "Protect the design — and your interest — on site, from mobilisation through to handover.",
    activities: ["Periodic site inspections and progress reporting", "Respond to contractor RFIs", "Review shop drawings, samples and mock-ups", "Certify progress payments against the work in place", "Snagging, practical completion and handover"],
    outputs: ["Site inspection reports", "RFI and shop-drawing responses", "Payment certificates", "Snagging list and handover documentation"],
    client: "Keep decisions timely — queries from site are time-critical.",
  },
};

/** Additional services — out-of-scope charges billed as-and-when instructed.
 *  These sit outside the professional fee; the schedule is issued with every
 *  quotation and engagement so the client knows the rate before instructing. */
export const DEFAULT_SERVICES: AdditionalService[] = [
  { key: "render",    label: "Additional 3D render",           blurb: "Photoreal render of a space beyond those already included in the interior fee (one per room).", unit: "image", rate: 250 },
  { key: "sitevisit", label: "Site visit",                     blurb: "Site inspection or meeting beyond the visits allowed for in the agreed programme.", unit: "visit", rate: 150 },
  { key: "expedited", label: "Unscheduled / expedited visit",  blurb: "Site attendance at short notice or outside the planned inspection programme.", unit: "visit", rate: 350 },
  { key: "variation", label: "Design variation",               blurb: "A change instructed after the relevant stage has been signed off.", unit: "change", rate: 450, included: 2 },
  { key: "printing",  label: "Printing & plotting",            blurb: "Large-format drawing prints or bound sets issued on request.", unit: "sheet", rate: 18 },
  { key: "authority", label: "Authority & permit submissions", blurb: "Preparation and lodging of submissions and liaison with approving authorities.", unit: "submission", rate: 400 },
];

/** Professional work phases — the fee disbursement schedule. Percentages of the total fee. */
export const DEFAULT_PHASES: FeePhase[] = [
  { key: "sd",     label: "Schematic Design", pct: 0.15 },
  { key: "dd",     label: "Design Development", pct: 0.20 },
  { key: "cd",     label: "Construction Documents (IFT)", pct: 0.40 },
  { key: "tender", label: "Tender / Bidding", pct: 0.05 },
  { key: "ca",     label: "Construction Administration", pct: 0.20, construction: true },
];

export const DEFAULT_RATECARD: RateCard = {
  disciplines: DEFAULT_DISCIPLINES, addons: DEFAULT_ADDONS, complexity: DEFAULT_COMPLEXITY, phases: DEFAULT_PHASES, services: DEFAULT_SERVICES, buildRates: DEFAULT_BUILD_RATES,
};

export interface EstimateConfig {
  budget: number;
  currency: string;
  complexity: string;
  disciplines: string[];
  addons: string[];
  discountPct?: number;      // studio discount, shown to the client
  /** Replace the %-of-fee construction (CA) amount with a flat fee. The client
   *  sees only the resulting figures — the comparison is studio-side. */
  constructionFixed?: number;
}

export interface EstimateLine { key: string; label: string; amount: number; detail: string; kind: "discipline" | "addon"; }
/** `pct` is the exact share (use it for bar widths); `pctLabel` is the whole-number
 *  share to display — apportioned so the shown figures sum to the real total
 *  rather than drifting to 99/101 through independent rounding. */
export interface EstimatePhase { key: string; label: string; amount: number; pct: number; pctLabel: number; construction?: boolean; cumulative: number; fixed?: boolean; }
export interface Estimate {
  lines: EstimateLine[];
  gross: number;
  discountPct: number;
  discount: number;
  total: number;
  pctOfBudget: number;
  phases: EstimatePhase[];
  constructionAmount: number;      // payable during construction (what the client sees)
  constructionPctAmount: number;   // what the %-of-fee basis would have been
  constructionIsFixed: boolean;    // true when a flat fee overrides the % basis
  constructionDelta: number;       // fixed − % basis (studio only; + = uplift)
}

export function defaultConfig(budget = 250000, currency = "USD"): EstimateConfig {
  return { budget, currency, complexity: "standard", disciplines: DEFAULT_DISCIPLINES.filter((d) => d.core).map((d) => d.key), addons: ["viz"], discountPct: 0 };
}

export function computeEstimate(c: EstimateConfig, rc: RateCard = DEFAULT_RATECARD): Estimate {
  const mult = rc.complexity.find((x) => x.key === c.complexity)?.mult ?? 1;
  const budget = Math.max(0, c.budget || 0);
  const lines: EstimateLine[] = [];
  // the card's fixed sums (floors, fixed-fee add-ons) are written in the card's
  // currency; a project priced in another has them converted, like the rates
  const k = fxFactor(rc.buildRateCurrency ?? DEFAULT_RATE_CURRENCY, c.currency, rc.fx);

  for (const d of rc.disciplines) {
    if (!c.disciplines.includes(d.key)) continue;
    const floor = Math.round(d.floor * k);
    const amount = Math.round(Math.max(budget * d.pct * mult, floor));
    lines.push({ key: d.key, label: d.label, amount, kind: "discipline", detail: `${(d.pct * 100).toFixed(1)}% · ${amount <= floor ? "floor" : "of budget"}` });
  }
  for (const a of rc.addons) {
    if (!c.addons.includes(a.key)) continue;
    if (a.requires && !c.disciplines.includes(a.requires)) continue;
    const amount = a.kind === "fixed" ? Math.round(a.value * k) : Math.round(budget * a.value * mult);
    lines.push({ key: a.key, label: a.label, amount, kind: "addon", detail: a.kind === "fixed" ? "fixed fee" : `${(a.value * 100).toFixed(1)}% of budget` });
  }

  const gross = lines.reduce((s, l) => s + l.amount, 0);
  const discountPct = Math.max(0, Math.min(100, c.discountPct || 0));
  const discount = Math.round(gross * discountPct / 100);
  const baseTotal = gross - discount;

  /* The design fee sets every stage on a % basis. A fixed construction fee then
     swaps out the CA stage(s) only — the earlier stages keep their amounts, and
     the total moves by the difference. */
  const consPhases = rc.phases.filter((p) => p.construction);
  const consPctSum = consPhases.reduce((s, p) => s + p.pct, 0);
  const constructionPctAmount = consPhases.reduce((s, p) => s + Math.round(baseTotal * p.pct), 0);
  const fixed = c.constructionFixed;
  const constructionIsFixed = typeof fixed === "number" && isFinite(fixed) && fixed >= 0;
  const constructionAmount = constructionIsFixed ? Math.round(fixed as number) : constructionPctAmount;
  const total = baseTotal - constructionPctAmount + constructionAmount;

  // Split a fixed fee across construction stages by their relative weight; the
  // last one absorbs the rounding remainder so the parts sum to the fee exactly.
  const consAmounts = new Map<string, number>();
  if (constructionIsFixed) {
    let assigned = 0;
    consPhases.forEach((p, i) => {
      const amt = i === consPhases.length - 1
        ? constructionAmount - assigned
        : (consPctSum > 0 ? Math.round(constructionAmount * (p.pct / consPctSum)) : 0);
      assigned += amt;
      consAmounts.set(p.key, amt);
    });
  }

  const amounts = rc.phases.map((p) => constructionIsFixed && p.construction ? (consAmounts.get(p.key) ?? 0) : Math.round(baseTotal * p.pct));

  /* Whole-number share labels by largest remainder. Target the rounded sum of the
     true shares (100 for a rate card whose phases total 100%), so the displayed
     percentages add up instead of drifting — and a misconfigured card still reads
     honestly low rather than being forced to 100. */
  const shares = amounts.map((a) => (total > 0 ? (a / total) * 100 : 0));
  const labels = shares.map((s) => Math.floor(s));
  let rem = Math.round(shares.reduce((a, b) => a + b, 0)) - labels.reduce((a, b) => a + b, 0);
  shares
    .map((s, i) => ({ i, frac: s - Math.floor(s) }))
    .sort((a, b) => b.frac - a.frac)
    .forEach((o) => { if (rem > 0) { labels[o.i]++; rem--; } });

  let cum = 0;
  const phases: EstimatePhase[] = rc.phases.map((p, i) => {
    const amount = amounts[i];
    cum += amount;
    return { key: p.key, label: p.label, amount, pct: total > 0 ? amount / total : 0, pctLabel: labels[i], construction: p.construction, cumulative: cum, fixed: constructionIsFixed && !!p.construction };
  });

  return {
    lines, gross, discountPct, discount, total, pctOfBudget: budget > 0 ? total / budget : 0, phases,
    constructionAmount, constructionPctAmount, constructionIsFixed, constructionDelta: constructionAmount - constructionPctAmount,
  };
}


/* ---- keeping the stage descriptions inside the appointment ----
   The stage outlines are written for a full multi-disciplinary commission. On a
   smaller appointment they over-promise, and an over-promise in a fee document
   is the kind of thing that gets read back to you later. */

const CONSULTANT_DISCIPLINES = ["structural", "mep", "landscape"];

/** The disciplines this client actually appointed. Falls back to "everything"
 *  only when no scope has been set at all, so an unconfigured project still
 *  reads sensibly. */
export function engagedDisciplines(p: any): Set<string> | null {
  /* An agreed fee schedule is the stronger evidence of scope, but it names its
     disciplines in prose ("Structural engineering"), so match on the key words
     rather than an exact key. Falls back to the estimator's own keys. */
  const billed: any[] = Array.isArray(p?.billing?.disciplines) ? p.billing.disciplines : [];
  if (billed.length) {
    const text = billed.map((d) => String(d?.name ?? d ?? "")).join(" ").toLowerCase();
    const found = new Set<string>();
    if (/architect/.test(text)) found.add("architecture");
    if (/interior/.test(text)) found.add("interior");
    if (/structur/.test(text)) found.add("structural");
    if (/mep|mechanical|electrical|plumb|services/.test(text)) found.add("mep");
    if (/landscap/.test(text)) found.add("landscape");
    if (found.size) return found;
  }
  const list: string[] | undefined = p?.estimate?.disciplines;
  if (!Array.isArray(list) || !list.length) return null;   // null = no scope on record, don't filter
  return new Set(list);
}

/** Resolve taggable stage lines against the appointment. */
export function scopedLines(lines: ScopedLine[], engaged: Set<string> | null): string[] {
  const out: string[] = [];
  for (const l of lines) {
    if (typeof l === "string") { out.push(l); continue; }
    if (!engaged) { out.push(l.text); continue; }
    const ok = l.needs === "any-consultant"
      ? CONSULTANT_DISCIPLINES.some((d) => engaged.has(d))
      : engaged.has(l.needs);
    if (ok) out.push(l.text);
  }
  return out;
}

/** The disciplines named in the IFT package, for prose that lists them. */
export function scopeDisciplineNames(engaged: Set<string> | null): string {
  if (!engaged) return "architecture, structure and MEP";
  const names = ["architecture", ...(engaged.has("structural") ? ["structure"] : []), ...(engaged.has("mep") ? ["MEP"] : [])];
  return names.length === 1 ? names[0] : names.slice(0, -1).join(", ") + " and " + names[names.length - 1];
}
